import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, startWith, take, takeWhile, shareReplay } from 'rxjs/operators';
import { NetzzuordnungService } from '../../service/netzzuordnung/netzzuordnung.service';
import { NetzzuordnungFilter } from '../../service/netzzuordnung/netzzuordnung-filter';
import { environment } from '../../../environments/environment';
import { NetzzuordnungExportService } from '../../service/netzzuordnung/netzzuordnung-export.service';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { TnbUser } from '../../service/auth/tnb-user';
import { AuthenticationService } from '../../service/auth/authentication.service';
import { TeilgebietSelectionService } from '../../service/common/teilgebiet/teilgebiet-selection.service';
import { Netzbetreiber } from '../../domain/model/netzbetreiber';

const FILTER_DEBOUNCE_TIME = 250;
const FILTER_OPTION_LIMIT = 50;

@Component({
    selector: 'tnb-netzzuordnungen-filter',
    templateUrl: './netzzuordnungen-filter.component.html',
    styleUrls: [
        './netzzuordnungen-filter.component.scss',
    ],
    standalone: false,
})
export class NetzzuordnungenFilterComponent implements OnInit, OnDestroy {
    private netzzuordnungService = inject(NetzzuordnungService);
    private netzbetreiberService = inject(NetzbetreiberService);
    private authService = inject(AuthenticationService);
    private netzzuordnungExportService = inject(NetzzuordnungExportService);
    private formBuilder = inject(FormBuilder);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    public ASSET_URL = environment.ASSET_URL;

    stadtwerkeId?: string;

    @Input() initialFilter?: NetzzuordnungFilter;
    @Output() filter = new EventEmitter<NetzzuordnungFilter>();

    filterFormGroup: FormGroup;

    filteredOrtsnamen$: Observable<string[]>;
    filteredStationsnamen$: Observable<string[]>;
    filteredTrafoFeldNamen$: Observable<string[]>;
    filteredUuid$: Observable<string[]>;
    filteredTechnischerPlatz$: Observable<string[]>;
    filteredNetzbetreiberName$: Observable<string[]>;
    filteredNetzbetreiberTeilgebiet$: Observable<string[]>;
    filteredNetzgruppe$: Observable<string[]>;
    filteredStadtwerkeId$: Observable<string[]>;
    public westnetzUserOrWestnetzAdmin = false; // true if the user has the Westnetz or Admin role

    private allNetzbetreiberForWestnetzView$: Observable<Netzbetreiber[]>;
    private componentDestroyed = false;

    async ngOnInit() {
        this.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();
        this.authService.user$.pipe(take(1)).subscribe(
            (usr: TnbUser) => {
                this.westnetzUserOrWestnetzAdmin = this.authService.isWestnetzUserOrWestnetzAdmin(usr);
                this.changeDetectorRef.markForCheck();
            },
        );

        this.allNetzbetreiberForWestnetzView$ =
            this.netzbetreiberService.getAlleNetzbetreiber()
                .pipe(
                    take(1),
                    shareReplay(1),
                );

        // Build form and set initial values
        this.filterFormGroup = this.formBuilder.group({
            stadtwerkeId: [
                this.initialFilter.stadtwerkeId,
            ],
            ortsname: [
                this.initialFilter.ortsname,
            ],
            stationsname: [
                this.initialFilter.stationsname,
            ],
            trafoFeldName: [
                this.initialFilter.trafoFeldName,
            ],
            uuid: [
                this.initialFilter.uuid,
            ],
            technischerPlatz: [
                this.initialFilter.technischerPlatz,
            ],
            netzbetreiberName: [
                this.initialFilter.netzbetreiberName,
            ],
            netzbetreiberTeilgebiet: [
                this.initialFilter.netzbetreiberTeilgebiet,
            ],
            uuidDate: this.formBuilder.group({
                start: [
                    this.initialFilter.uuidDateStart,
                ],
                end: [
                    this.initialFilter.uuidDateEnd,
                ],
            }),
            netzgruppe: [
                this.initialFilter.netzgruppe,
            ],
        });

        this.filterFormGroup.valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.value),
                takeWhile(() => !this.componentDestroyed),
                map(val => {
                    return new NetzzuordnungFilter(
                        val.stadtwerkeId || this.stadtwerkeId,
                        val.ortsname,
                        val.stationsname,
                        val.trafoFeldName,
                        val.uuid,
                        val.uuidDate?.start,
                        val.uuidDate?.end?.endOf('day'),
                        val.technischerPlatz,
                        val.netzbetreiberName,
                        val.netzbetreiberTeilgebiet,
                        val.netzgruppe,
                    );
                }),
            )
            .subscribe(val => this.filter.next(val));

        this.filteredOrtsnamen$ = this.filterFormGroup.get('ortsname').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('ortsname').value),
                distinctUntilChanged(),
                switchMap(ort => this.netzzuordnungService.getOrtsnamen(this.stadtwerkeId, ort)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredStationsnamen$ = this.filterFormGroup.get('stationsname').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('stationsname').value),
                distinctUntilChanged(),
                switchMap(stationsname => this.netzzuordnungService.getStationsnamen(
                    this.stadtwerkeId,
                    this.filterFormGroup.get('ortsname').value,
                    stationsname,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredTrafoFeldNamen$ = this.filterFormGroup.get('trafoFeldName').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('trafoFeldName').value),
                distinctUntilChanged(),
                switchMap(trafoFeldName => this.netzzuordnungService.getTrafoFeldNamen(
                    this.stadtwerkeId,
                    this.filterFormGroup.get('ortsname').value,
                    this.filterFormGroup.get('stationsname').value,
                    trafoFeldName,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredUuid$ = this.filterFormGroup.get('uuid').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('uuid').value),
                distinctUntilChanged(),
                switchMap(uuid => this.netzzuordnungService.getUuid(this.stadtwerkeId, uuid)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredTechnischerPlatz$ = this.filterFormGroup.get('technischerPlatz').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('technischerPlatz').value),
                distinctUntilChanged(),
                switchMap(technischerPlatz => this.netzzuordnungService.getNetzzuordnungId(
                    this.stadtwerkeId,
                    technischerPlatz,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredNetzbetreiberName$ = this.filterFormGroup.get('netzbetreiberName').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('netzbetreiberName').value),
                map(name => name?.toLowerCase() || ''),
                distinctUntilChanged(),
                switchMap(name => {
                    return this.allNetzbetreiberForWestnetzView$.pipe(
                        map(allNetzbetreiber =>
                            name ? allNetzbetreiber.filter(n => n.name.toLowerCase().includes(name)) : allNetzbetreiber,
                        ),
                    );
                }),
                map(netzbetreiber => netzbetreiber.map(n => n.name)),
                map(options => {
                    return [
                        ...new Set(options),
                    ];
                }),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredNetzbetreiberTeilgebiet$ = this.filterFormGroup.get('netzbetreiberTeilgebiet').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('netzbetreiberTeilgebiet').value),
                map(teilgebiet => teilgebiet?.toLowerCase() || ''),
                distinctUntilChanged(),
                switchMap(teilgebiet => {
                    return this.allNetzbetreiberForWestnetzView$.pipe(
                        map(allNetzbetreiber =>
                            teilgebiet ? allNetzbetreiber.filter(n => n.teilgebiet.toLowerCase().includes(teilgebiet)) : allNetzbetreiber,
                        ),
                    );
                }),
                map(netzbetreiber => netzbetreiber.map(n => n.teilgebiet)),
                map(options => {
                    return [
                        ...new Set(options),
                    ];
                }),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredNetzgruppe$ = this.filterFormGroup.get('netzgruppe').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('netzgruppe').value),
                distinctUntilChanged(),
                switchMap(netzgruppe => this.netzzuordnungService.getNetzgruppe(
                    this.stadtwerkeId,
                    netzgruppe,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredStadtwerkeId$ = this.filterFormGroup.get('stadtwerkeId').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('stadtwerkeId').value),
                map(stadtwerkeIdQuery => stadtwerkeIdQuery?.toLowerCase() || ''),
                distinctUntilChanged(),
                switchMap(stadtwerkeIdQuery => {
                    return this.allNetzbetreiberForWestnetzView$.pipe(
                        map(allNetzbetreiber =>
                            stadtwerkeIdQuery ? allNetzbetreiber.filter(n => n.stadtwerkeId.toLowerCase().includes(stadtwerkeIdQuery)) : allNetzbetreiber,
                        ),
                    );
                }),
                map(netzbetreiber => netzbetreiber.map(n => n.stadtwerkeId)),
                map(options => {
                    return [
                        ...new Set(options),
                    ];
                }),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    public exportNetzzuordnungen() {
        this.netzzuordnungExportService.exportNetzzuordnungen(
            this.teilgebietSelectionService.getSelectedTeilgebiet(),
        );
    }
}
